import { useEffect, useState } from 'react'
import { useAppDispatch } from '../../reducers/store'
import { fetchEventsForDevice, selectDevices } from '../../reducers/devices'
import { useSelector } from 'react-redux'
import DataGrid from '../data-grid/DataGrid'
import PageSkeleton from '../skeleton/PageSkeleton'
import { Pagination } from '../pagination/Pagination'
import { DEFAULT_PAGE, MAXIMUM_PAGE_SIZE } from '../../strings/api-consts'
import { useParams } from 'react-router-dom'
import { PaginationSelectOption } from '../../types/MemberTypes'
import { deviceEventColumns } from './DeviceEventColumns'
import {
  DeviceEvent,
  DeviceEventCategory,
  DeviceEventType,
  getCategoryByEventType,
  getCategoryName,
} from '../../types/DeviceEventTypes'
import { CrossCircledIcon, MagnifyingGlassIcon } from '@radix-ui/react-icons'
import { Button, Card, IconButton, TextField } from '@radix-ui/themes'
import { Input } from '../input/Input'
import DeviceEventFilterOption from './DeviceEventFilterOption'
import { selectUser } from '../../reducers/users'
import { inPov } from '../../utils/session'

interface IDeviceOverviewEventsProps {
  setPopoutData: (data: DeviceEvent) => void
  setShowPopout: (show: boolean) => void
}

function DeviceOverviewEvents(props: IDeviceOverviewEventsProps) {
  const { setPopoutData, setShowPopout } = props
  const defaultSortOrder = 'desc'
  const defaultSortColumn = 'eventTime'
  const showSearch = false

  const di = useAppDispatch()
  const { session } = useSelector(selectUser)
  const { deviceId } = useParams()
  const { deviceEvents, totalPagesDeviceEvents, currentPageDeviceEvents } = useSelector(selectDevices)
  const [dataLoading, setDataLoading] = useState<boolean>(true)
  const [selectedTypes, setSelectedTypes] = useState<DeviceEventType[]>([])
  const [fromDateTime, setFromDateTime] = useState<string>('')
  const [toDateTime, setToDateTime] = useState<string>('')
  const [sortOrder, setSortOrder] = useState<string>(defaultSortOrder)
  const [sortColumn, setSortColumn] = useState<string>(defaultSortColumn)
  const [searchFilter, setSearchFilter] = useState<string>('')
  const [isDirty, setIsDirty] = useState<boolean>(false)
  const [eventsToFilterOut, setEventsToFilterOut] = useState<DeviceEventType[]>([])
  const [defaultEvents, setDefaultEvents] = useState<DeviceEventType[]>(
    Object.values(DeviceEventType).filter((type) => type !== DeviceEventType.SCAN_RECEIVED)
  )

  useEffect(() => {
    if (defaultEvents.length > 0 && deviceId) {
      setDataLoading(true)
      di(
        fetchEventsForDevice(
          DEFAULT_PAGE,
          MAXIMUM_PAGE_SIZE,
          deviceId!,
          defaultEvents,
          fromDateTime,
          toDateTime,
          searchFilter,
          sortColumn,
          sortOrder
        )
      )
    }
    setSelectedTypes(defaultEvents)
  }, [defaultEvents])

  useEffect(() => {
    if (deviceEvents || (deviceEvents && (deviceEvents as DeviceEvent[]).length === 0)) {
      setDataLoading(false)
      setIsDirty(false)
    }
  }, [deviceEvents])

  useEffect(() => {
    if (!dataLoading) {
      search()
    }
  }, [sortColumn, sortOrder])

  useEffect(() => {
    if (session && session.portalPreferences.sensitiveTravel !== true) {
      setEventsToFilterOut([DeviceEventType.COUNTRY_CODE, DeviceEventType.TELCO, DeviceEventType.TELCO_SET, DeviceEventType.COUNTRY_CODE_SET])
    }
  }, [session])

  useEffect(() => {
    setDefaultEvents(defaultEvents.filter((type) => !eventsToFilterOut.includes(type)))
  }, [eventsToFilterOut])

  const search = () => {
    setDataLoading(true)
    di(
      fetchEventsForDevice(
        DEFAULT_PAGE,
        MAXIMUM_PAGE_SIZE,
        deviceId!,
        selectedTypes,
        fromDateTime,
        toDateTime,
        searchFilter,
        sortColumn,
        sortOrder
      )
    )
  }

  const resetFilters = () => {
    setSelectedTypes(defaultEvents)
    setFromDateTime('')
    setToDateTime('')
    setSearchFilter('')
    di(
      fetchEventsForDevice(DEFAULT_PAGE, MAXIMUM_PAGE_SIZE, deviceId!, defaultEvents, '', '', '', sortColumn, sortOrder)
    )
  }

  const sortBy = (column: string) => {
    if (sortColumn === column) {
      if (sortOrder === 'desc' && column !== defaultSortColumn) {
        // Clear Sort
        setSortColumn(defaultSortColumn)
        setSortOrder(defaultSortOrder)
      } else {
        // Sort Existing Column
        setSortOrder(sortOrder == 'desc' ? 'asc' : 'desc')
      }
    } else {
      // Sort New Column
      setSortColumn(column)
      setSortOrder('asc')
    }
  }

  const handlePageClick = (data: PaginationSelectOption) => {
    setDataLoading(true)
    di(
      fetchEventsForDevice(
        data.selected + 1,
        MAXIMUM_PAGE_SIZE,
        deviceId!,
        selectedTypes,
        fromDateTime,
        toDateTime,
        searchFilter,
        sortColumn,
        sortOrder
      )
    )
  }

  return (
    <>
      {dataLoading ? (
        <div className="flex justify-center items-center min-h-screen">
          <PageSkeleton />
        </div>
      ) : (
        <>
          {deviceEvents &&
          deviceEvents.length === 0 &&
          fromDateTime == '' &&
          toDateTime == '' &&
          selectedTypes.length == 0 &&
          (searchFilter == '' || searchFilter == undefined) &&
          !isDirty ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center -mt-32">
                <div className="flex items-center justify-center">
                  <MagnifyingGlassIcon className="h-12 w-12" />
                </div>
                <p className="font-bold">No Events</p>
                <p className="text-sm">This device has no events</p>
              </div>
            </div>
          ) : (
            <div className="flex gap-4">
              <Card
                className={`flex flex-col gap-2 min-w-[270px] h-fit p-4 ${inPov(session) ? 'h-[calc(100vh_-_190px)]' : 'h-[calc(100vh_-_180px)]'} overflow-y-auto`}
              >
                {showSearch && (
                  <div>
                    <TextField.Root
                      value={searchFilter}
                      placeholder="Search..."
                      className="w-full h-[38px] mr-4 bg-inputBackground text-mainText dark:shadow-none border border-transparent dark:border-neutral-11 hover:shadow-none hover:border hover:border-borderPurple hover:dark:border-borderPurple focus:ring-mainBorder focus:ring-borderPurple p-0"
                      onChange={(e) => {
                        setSearchFilter(e.target.value)
                        setIsDirty(true)
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          search()
                        }
                      }}
                    >
                      <TextField.Slot className="p-0"></TextField.Slot>
                      {searchFilter !== '' && (
                        <TextField.Slot>
                          <IconButton
                            size="1"
                            variant="ghost"
                            onClick={() => {
                              setSearchFilter('')
                              di(
                                fetchEventsForDevice(
                                  DEFAULT_PAGE,
                                  MAXIMUM_PAGE_SIZE,
                                  deviceId!,
                                  selectedTypes,
                                  fromDateTime,
                                  toDateTime,
                                  '',
                                  sortColumn,
                                  sortOrder
                                )
                              )
                            }}
                          >
                            <CrossCircledIcon height="14" width="14" className="cursor-pointer dark:text-white" />
                          </IconButton>
                        </TextField.Slot>
                      )}
                      <TextField.Slot>
                        <IconButton
                          size="1"
                          variant="ghost"
                          className="cursor-pointer dark:text-white"
                          onClick={() => search()}
                        >
                          <MagnifyingGlassIcon height="14" width="14" />
                        </IconButton>
                      </TextField.Slot>
                    </TextField.Root>
                  </div>
                )}
                <div className="flex flex-col gap-2 flex-1 overflow-auto">
                  <div className="flex flex-col gap-1">
                    <label className="text-neutral-11 dark:text-neutral-6 text-xs font-medium tracking-widest pb-1">
                      Filter by Event
                    </label>
                    {Object.values(DeviceEventCategory).map((cat: DeviceEventCategory) => {
                      return (
                        <DeviceEventFilterOption
                          key={cat}
                          eventsToFilterOut={eventsToFilterOut}
                          category={cat}
                          selectedTypes={selectedTypes}
                          setIsDirty={setIsDirty}
                          setTypes={(val) => {
                            setSelectedTypes(val)
                          }}
                          open={
                            selectedTypes.filter((type) => getCategoryByEventType(type) === getCategoryName(cat))
                              .length > 0 &&
                            selectedTypes.filter((type) => getCategoryByEventType(type) === getCategoryName(cat))
                              .length !==
                              Object.values(DeviceEventType).filter(
                                (type) =>
                                  getCategoryByEventType(type) === getCategoryName(cat) &&
                                  !eventsToFilterOut.includes(type)
                              ).length
                          }
                        />
                      )
                    })}
                  </div>
                </div>
                <div className="flex flex-col gap-1 left-0 w-full">
                  <div className="flex flex-col gap-1">
                    <div>
                      <label className="uppercase text-neutral-11 dark:text-neutral-6 text-xs font-medium tracking-widest pb-1">
                        From (UTC)
                      </label>
                      <Input
                        value={fromDateTime}
                        onChange={(e) => {
                          setFromDateTime(e)
                          setIsDirty(true)
                        }}
                        type="datetime-local"
                      />
                    </div>
                    <div>
                      <label className="uppercase text-neutral-11 dark:text-neutral-6 text-xs font-medium tracking-widest pb-1">
                        To (UTC)
                      </label>
                      <Input
                        value={toDateTime}
                        onChange={(e) => {
                          setToDateTime(e)
                          setIsDirty(true)
                        }}
                        type="datetime-local"
                      />
                    </div>
                  </div>
                  <Button
                    className="bg-mainPurple hover:bg-tertiaryPurple active:active:bg-tertiaryPurple h-[38px] cursor-pointer w-full my-2"
                    onClick={() => search()}
                  >
                    Filter Results
                  </Button>
                  <div className="px-2">
                    <Button
                      className="text-mainPurple dark:text-secondaryPurple hover:text-tertiaryPurple active:text-tertiaryPurple cursor-pointer w-full"
                      variant="ghost"
                      onClick={() => {
                        resetFilters()
                      }}
                    >
                      Reset Filters
                    </Button>
                  </div>
                </div>
              </Card>
              <div className="mx-auto w-full pr-8 overflow-y-auto">
                {deviceEvents && deviceEvents.length > 0 ? (
                  <>
                    <DataGrid
                      columns={deviceEventColumns(setShowPopout, setPopoutData)}
                      data={deviceEvents}
                      loading={dataLoading}
                      sortFunction={(column: string) => {
                        sortBy(column)
                      }}
                      sortColumn={sortColumn}
                      sortOrder={sortOrder}
                      headerClass="bg-tableHeaderGrey sticky top-0"
                      rowClass="!bg-tableRowGrey"
                      maxWidth="120px"
                    />
                    <div className="mt-2">
                      <Pagination
                        totalPages={totalPagesDeviceEvents}
                        forcePage={currentPageDeviceEvents}
                        handlePageClick={handlePageClick}
                      />
                    </div>
                  </>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <div className="flex items-center justify-center">
                        <MagnifyingGlassIcon className="h-12 w-12" />
                      </div>
                      <p className="font-bold">No Events</p>
                      <p className="text-sm">This device has no events for filter criteria</p>
                      <Button
                        className="mt-4 text-center flex bg-black mx-auto h-auto min-h-8 cursor-pointer outline-1 dark:outline dark:outline-white"
                        onClick={() => resetFilters()}
                      >
                        Reset Filters
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </>
  )
}

export default DeviceOverviewEvents
